var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PauseContext — provides derived pause status to all dashboard cards.
 */
import React, { createContext, useContext, useMemo } from 'react';
var defaultCtx = {
    rawFlags: { profile: false, device: false, notification: false },
    pauseStatus: { profile: false, device: false, notification: false },
    isProfilePaused: false,
    isDevicePaused: false,
    isNotificationPaused: false,
    isAnyPaused: false,
    devicePauseRange: { start: null, end: null },
    refreshPauseStatus: function () { },
};
export var PauseContext = createContext(defaultCtx);
/**
 * EXPORTED: Derives a single disabled-date range for the device calendar.
 */
export var deriveDevicePauseRange = function (auditInfo) {
    if (!auditInfo)
        return { start: null, end: null };
    var rawFlags = auditInfo.rawFlags, profileActualPausedDate = auditInfo.profileActualPausedDate, deviceActualPausedDate = auditInfo.deviceActualPausedDate, profileHistory = auditInfo.profileHistory, deviceHistory = auditInfo.deviceHistory;
    // 1. Currently active pause(s) — open-ended
    var activeStarts = [];
    if ((rawFlags === null || rawFlags === void 0 ? void 0 : rawFlags.profile) && profileActualPausedDate)
        activeStarts.push(profileActualPausedDate);
    if ((rawFlags === null || rawFlags === void 0 ? void 0 : rawFlags.device) && deviceActualPausedDate)
        activeStarts.push(deviceActualPausedDate);
    if (activeStarts.length) {
        var earliestStart = activeStarts.reduce(function (min, cur) {
            return new Date(cur).getTime() < new Date(min).getTime() ? cur : min;
        });
        return { start: earliestStart, end: null };
    }
    // 2. No active pause — fall back to most recently completed window
    var relevant = __spreadArray(__spreadArray([], (profileHistory !== null && profileHistory !== void 0 ? profileHistory : []), true), (deviceHistory !== null && deviceHistory !== void 0 ? deviceHistory : []), true).filter(function (row) { return row.pauseEndDateRaw; });
    if (!relevant.length)
        return { start: null, end: null };
    var latest = relevant.reduce(function (mostRecent, row) {
        return new Date(row.pauseEndDateRaw).getTime() > new Date(mostRecent.pauseEndDateRaw).getTime()
            ? row
            : mostRecent;
    });
    return {
        start: latest.pauseStartDateRaw || null,
        end: latest.pauseEndDateRaw || null,
    };
};
export var PauseProvider = function (_a) {
    var auditInfo = _a.auditInfo, pauseInfo = _a.pauseInfo, refreshPauseStatus = _a.refreshPauseStatus, children = _a.children;
    var value = useMemo(function () {
        var _a, _b;
        var rawFlags = (_a = auditInfo === null || auditInfo === void 0 ? void 0 : auditInfo.rawFlags) !== null && _a !== void 0 ? _a : defaultCtx.rawFlags;
        var pauseStatus = (_b = auditInfo === null || auditInfo === void 0 ? void 0 : auditInfo.pauseStatus) !== null && _b !== void 0 ? _b : defaultCtx.pauseStatus;
        var devicePauseRange = deriveDevicePauseRange(auditInfo);
        return {
            rawFlags: rawFlags,
            pauseStatus: pauseStatus,
            isProfilePaused: pauseStatus.profile,
            isDevicePaused: pauseStatus.device,
            isNotificationPaused: pauseStatus.notification,
            isAnyPaused: pauseStatus.profile || pauseStatus.device || pauseStatus.notification,
            devicePauseRange: devicePauseRange,
            refreshPauseStatus: refreshPauseStatus !== null && refreshPauseStatus !== void 0 ? refreshPauseStatus : defaultCtx.refreshPauseStatus,
        };
    }, [auditInfo, pauseInfo, refreshPauseStatus]);
    return _jsx(PauseContext.Provider, { value: value, children: children });
};
export var usePauseStatus = function () { return useContext(PauseContext); };
export var PausedOverlay = function (_a) {
    var disabled = _a.disabled, reason = _a.reason, children = _a.children;
    if (!disabled)
        return _jsx(_Fragment, { children: children });
    return (_jsxs("div", { style: { position: 'relative' }, children: [_jsx("div", { style: { opacity: 0.45, pointerEvents: 'none', userSelect: 'none' }, children: children }), _jsx("div", { style: {
                    position: 'absolute',
                    inset: 0,
                    background: 'transparent',
                    zIndex: 10,
                    pointerEvents: 'all',
                    cursor: 'not-allowed',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                    padding: '8px',
                }, children: reason && (_jsx("div", { style: {
                        background: '#eb921f',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '3px 10px',
                        borderRadius: '20px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                        letterSpacing: '0.3px',
                        whiteSpace: 'nowrap',
                        opacity: 1,
                    }, children: reason })) })] }));
};
export var useCardPauseDisabled = function (cardType) {
    var _a = usePauseStatus(), isProfilePaused = _a.isProfilePaused, isDevicePaused = _a.isDevicePaused, isNotificationPaused = _a.isNotificationPaused;
    switch (cardType) {
        case 'profile':
            return { disabled: isProfilePaused, reason: 'Member Paused — View Only' };
        case 'device':
            return {
                disabled: isProfilePaused || isDevicePaused,
                reason: isProfilePaused ? 'Member Paused — View Only' : 'Device Paused — Data is View Only',
            };
        case 'notification':
            return {
                disabled: isProfilePaused || isNotificationPaused,
                reason: isProfilePaused ? 'Member Paused — View Only' : 'Notifications Paused — View Only',
            };
        default:
            return { disabled: false, reason: '' };
    }
};
